/// <amd-module name="VC/Common/Providers/VersionDropdownProvider" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IPickListAction, IPickListGroup, IPickListPivot, IPickListItem } from "VSSUI/PickList";
import { VssIconType } from "VSSUI/VssIcon";

import { Favorite } from "Favorites/Common/Contracts/Favorite";
import { IArtifactPickerProviderOptions, ArtifactPickerProviderBase, IFavoritesContext } from "Favorites/Picker/ArtifactPickerProvider";
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import * as Resources from "VC/Common/Resources";

import { IVersionDropdownItem, IVersionDropdownOptions } from "../Components/VersionDropdown.Props";
import { GitRef, GitRepository, GitCommitRef, GitQueryCommitsCriteria } from "../Contracts/Git";
import { GitRefFavoritesService, GitRefFavoriteArtifactConstants } from "../GitRefFavoritesService";
import { IDeprecatedGitRefService, PagedList } from "../GitRefService";
import { IGitRestClient } from "../RestClient/Git";
import * as RefUtils from "../Utils/Ref";
import { reuseIfStrictlyStronger, debouncePromiseFunction, SearchType, SearchPivotType, composeSearchByPivot, createValidator } from "../Utils/Search";
import { startTelemetryTimer } from "../Utils/Telemetry";

type SearchItemsType = SearchType<IVersionDropdownItem[]>;
type SearchPivotItemsType = SearchPivotType<IVersionDropdownItem[]>;

const minCommitCharacters = 4;
const maxCommitResults = 10;
const isValidCommitIdRegex = /^[0-9a-f]+$/i;
const telemetryFeatureName = "VersionDropdownProvider";

const enum GroupIds {
    repositoryDefault = "default",
    mineBranches = "favorites",
    allBranches = "all-branches",
    allTags = "all-tags",
    foundCommits = "found-commits",
}

const enum PivotIds {
    branches = "branches",
    tags = "tags",
    commits = "commits",
}

/**
 * This is a special token that will return every ref ("!" is before every "ref/*" name)
 * while it allows us to declare the mine refs as a PagedList with continuation.
 */
const mineContinuationToken = "!";

export class VersionDropdownProvider extends ArtifactPickerProviderBase<IVersionDropdownItem> {
    private _artifactPickerOptions: IArtifactPickerProviderOptions<IVersionDropdownItem>;
    private _gitRefFavoritesService: GitRefFavoritesService;
    private _continuationTokens: { [groupKey: string]: string | undefined } = {};
    private _ariaLabelPrefix?: string;

    /**
     * Display search box always.
     */
    public minItemsForSearchBox = 0;

    public selectExactMatchOnEnter = true;
    
    constructor(
        private pageContext: IVssPageContext,
        private options: IVersionDropdownOptions,
    ) {
        super();

        // These values may not be valid for the standard favorites service, but we'll consume them in the GitRefFavoritesService instead.
        const favoritesContext: IFavoritesContext = {
            artifactType: GitRefFavoriteArtifactConstants.artifactType,
            artifactScope: {
                id: options.repository.id,
                name: options.repository.name,
                type: GitRefFavoriteArtifactConstants.artifactScopeType,
            }
        };

        const iconByKind = {
            branch: "OpenSource",
            tag: "Tag",
            commit: "BranchCommit",
        };

        let otherActions;
        let pivots: IPickListPivot[] | undefined = createPivots(options);
        let searchTextPlaceholder: string | undefined = undefined;
        if (pivots && pivots.length === 1) {
            const getActions = pivots[0].getActions;
            otherActions = getActions && getActions([]);
            searchTextPlaceholder = pivots[0].searchTextPlaceholder;
            pivots = undefined;
        }

        const { id: projectId } = options.repository.project;
        this._gitRefFavoritesService = this.getGitRefFavoritesService(projectId, options.viewMyBranches);
        this._artifactPickerOptions = {
            pageContext: pageContext,
            favoritesContext,
            favoritesService: this._gitRefFavoritesService,
            onArtifactClicked: item => options.onVersionSelected && options.onVersionSelected(item.versionString),
            selectedArtifact: getItemFromVersionString(options.selectedVersionString, options.repository),
            hideFavoriteSelectedItemIndicator: true,
            getArtifacts: this.getPickListItems,
            getArtifactId: item => item.refName,
            getArtifactName: item => item.friendlyName,
            getArtifactIcon: item => ({
                iconType: VssIconType.fabric,
                iconName: iconByKind[item.kind],
            }),
            getFavoriteFromArtifact,
            getArtifactListGroupId,
            artifactComparer,
            groups: createGroups(options, { [GroupIds.allBranches]: true, [GroupIds.allTags]: true }),
            pivots,
            searchTextPlaceholder,
            otherActions,
            limitSearchToPivot: true,
            getSearchResults: this.createSearchFunction(),
            getSearchResultsContinuationToken: (list: PagedList<IVersionDropdownItem>) => list.continuationToken,
            onFilterItemByText: filterItemByText,
        };

        this.initialize(this._artifactPickerOptions);

        // Tags are not cached, so we only need to notify of changes in branches.
        this.getGitRefService().registerBranchesChangedListener(this.forgetCachedArtifacts);

        this._ariaLabelPrefix = options.ariaLabel;
    }

    public get ariaLabelFormat() {
        // default to branch
        let selectedItemTypeAriaLabel = Resources.VersionDropdown_BranchAriaLabel;
        if (this.selectedItem) {
            if (this.selectedItem.kind === "commit") {
                selectedItemTypeAriaLabel = Resources.VersionDropdown_CommitAriaLabel;
            } else if (this.selectedItem.kind === "tag") {
                selectedItemTypeAriaLabel = Resources.VersionDropdown_TagAriaLabel;
            }
        }
        
        if (this._ariaLabelPrefix) {
            return `${this._ariaLabelPrefix} ${selectedItemTypeAriaLabel}`;
        } else {
            return selectedItemTypeAriaLabel;
        }
    }

    public dispose(): void {
        super.dispose();

        this.getGitRefService().unregisterBranchesChangedListener(this.forgetCachedArtifacts);
    }

    private getPickListItems = async (groupKey?: string): Promise<IVersionDropdownItem[]> => {
        if (!groupKey) {
            // This provider relies on onGroupLoadRequest even for initial load
            return [];
        }

        const telemetryTimer = startTelemetryTimer(this.pageContext, telemetryFeatureName);
        const refs = await this.getRefs(groupKey);

        this._continuationTokens[groupKey] = refs.continuationToken;

        if (!refs.continuationToken) {
            const loadingState = this.getGroupsLoadingState();
            if (loadingState[groupKey]) {
                loadingState[groupKey] = false;
                // We need to create new instances, otherwise we'd be changing the existing props
                // so PickList could not detect any change in groups to re-render.
                this._artifactPickerOptions.groups = createGroups(this.options, loadingState);
            }
        }

        // This assumes ArtifactPickerProvider has already called getFavorites, otherwise getCurrentFolderFavorites would throw
        const folderFavorites = this.options.viewMyBranches
            ? await this._gitRefFavoritesService.getCurrentFolderFavorites()
            : [];

        const items = refs.map(ref => createRefDropdownItem(ref, this.options.repository, this.options.currentIdentityId, folderFavorites));

        if (this.selectedItem && this.selectedItem.kind === "commit") {
            items.push(this.selectedItem);
        }

        telemetryTimer.publishTime("getPickListItems", { groupKey, count: items.length, hasMore: Boolean(refs.continuationToken) });

        return items;
    }

    private getRefs(groupKey: string): Promise<PagedList<GitRef>> {
        const continuationToken = this._continuationTokens[groupKey];
        const { id: repositoryId, project: { id: projectId } } = this.options.repository;

        if (continuationToken) {
            return groupKey === GroupIds.allTags
                ? this.getGitRefService().getMoreTags(repositoryId, projectId, continuationToken)
                : this.getGitRefService().getMoreBranches(repositoryId, projectId, continuationToken);
        } else {
            return groupKey === GroupIds.allTags
                ? this.getGitRefService().getPagedTags(repositoryId, projectId)
                : this.options.viewMyBranches
                    ? this.getMyBranches()
                    : this.getGitRefService().getPagedBranches(repositoryId, projectId);
        }
    }

    private getMyBranches(): Promise<PagedList<GitRef>> {
        const { id: repositoryId, project: { id: projectId } } = this.options.repository;
        return this.getGitRefService().getMyBranches(repositoryId, projectId)
            .then(refs => new PagedList(refs, mineContinuationToken));
    }

    private getGitRefFavoritesService(projectId: string, viewMine: boolean): GitRefFavoritesService {
        const standardFavoritesService = this.pageContext.getService<IFavoritesService>("IFavoritesService");
        const gitRestClient = this.pageContext.getRestClient<IGitRestClient>("IGitRestClient");
        return new GitRefFavoritesService(
            gitRestClient,
            viewMine && standardFavoritesService.canUseFavorites(),
            projectId,
        );
    }

    private getGitRefService(): IDeprecatedGitRefService {
        return this.pageContext.getService<IDeprecatedGitRefService>("IDeprecatedGitRefService");
    }

    private createSearchFunction(): SearchPivotItemsType {
        const delayBeforeOnlineSearchMs = 1000;
        const debounce = (fn: SearchItemsType) => debouncePromiseFunction(fn, delayBeforeOnlineSearchMs, []);
        const trim = (fn: SearchItemsType) =>
            (searchText: string, continuationToken: string) => fn(searchText && searchText.trim(), continuationToken);

        const abortIfBranchesFullyLoaded = (fn: SearchItemsType) =>
            (searchText: string, continuationToken: string) =>
                this.isGroupFullyLoaded(GroupIds.allBranches)
                    ? Promise.resolve(undefined)
                    : fn(searchText, continuationToken);

        const abortIfTagsFullyLoaded = (fn: SearchItemsType) =>
            (searchText: string, continuationToken: string) =>
                this.isGroupFullyLoaded(GroupIds.allTags)
                    ? Promise.resolve(undefined)
                    : fn(searchText, continuationToken);

        const reuseLastRefs = (fn: SearchItemsType) => reuseIfStrictlyStronger(fn, filterRefsInMemory, areRefsResultsComplete);

        const validateSearchCommits = (fn: SearchItemsType) => createValidator(fn, validateCommitsSearch);
        const reuseLastCommits = (fn: SearchItemsType) => reuseIfStrictlyStronger(fn, filterCommitsInMemory, areCommitResultsComplete);

        return composeSearchByPivot(
            {
                [PivotIds.branches]: abortIfBranchesFullyLoaded(trim(reuseLastRefs(debounce(this.searchBranchRefs)))),
                [PivotIds.tags]: abortIfTagsFullyLoaded(trim(reuseLastRefs(debounce(this.searchTagRefs)))),
                [PivotIds.commits]: trim(validateSearchCommits(reuseLastCommits(debounce(this.searchCommits)))),
            },
            PivotIds.branches);
    }

    private searchCommits = async (searchText: string): Promise<IVersionDropdownItem[]> => {
        const telemetryTimer = startTelemetryTimer(this.pageContext, telemetryFeatureName);
        const gitRestClient = this.pageContext.getRestClient<IGitRestClient>("IGitRestClient");
        const { id: repositoryId, project: { id: projectId } } = this.options.repository;

        const commits = await gitRestClient.getCommitsBatch(
            {
                fromCommitId: padEnd(searchText, "0", 40),
                toCommitId: padEnd(searchText, "F", 40),
            } as GitQueryCommitsCriteria,
            repositoryId,
            projectId,
            0,
            maxCommitResults + 1);

        const items = commits.map(commit => createCommitDropdownItem(commit, this.options.repository));
        telemetryTimer.publishTime("searchCommits", { count: items.length });

        return items;
    }

    private searchBranchRefs = (searchText: string, continuationToken?: string): Promise<IVersionDropdownItem[] | undefined> =>
        this.searchRefs(searchText, continuationToken, GroupIds.allBranches, this.getGitRefService().searchBranches);

    private searchTagRefs = (searchText: string, continuationToken?: string): Promise<IVersionDropdownItem[] | undefined> =>
        this.searchRefs(searchText, continuationToken, GroupIds.allTags, this.getGitRefService().searchTags);

    private async searchRefs(
        searchText: string,
        continuationToken: string | undefined,
        groupKey: GroupIds,
        searchFunction: (repositoryId: string, projectId: string, searchText: string, continuationToken?: string) => Promise<PagedList<GitRef>>,
    ): Promise<IVersionDropdownItem[] | undefined> {
        const telemetryTimer = startTelemetryTimer(this.pageContext, telemetryFeatureName);

        if (!continuationToken) {
            // If we're continuing a previous search, use given continuationToken.
            // Otherwise, it's the first search page, so don't fetch items that are already on the picker, let's steal the all's continuationToken.
            continuationToken = this._continuationTokens[groupKey];
        }

        const { id: repositoryId, project: { id: projectId } } = this.options.repository;
        const refs = await searchFunction(repositoryId, projectId, searchText, continuationToken);
        const items = refs.map(ref => createRefDropdownItem(ref, this.options.repository, this.options.currentIdentityId, []));
        telemetryTimer.publishTime("searchRefs", { count: items.length, hasMore: Boolean(refs.continuationToken) });
        return new PagedList(items, refs.continuationToken);
    }

    private getGroupsLoadingState(): { [groupKey: string]: boolean } {
        if (this._artifactPickerOptions && this._artifactPickerOptions.groups) {
            return (<IPickListGroup[]>this._artifactPickerOptions.groups).reduce(
                (state, group) => ({
                    ...state,
                    [group.key]: group.isLoading,
                }),
                {});
        } else {
            return {};
        }
    }

    private isGroupFullyLoaded(groupKey: GroupIds): boolean {
        return !this.getGroupsLoadingState()[groupKey];
    }
}

function createGroups(
    { viewMyBranches, viewTagsPivot, viewCommitsPivot }: IVersionDropdownOptions,
    loadingState: { [groupKey: string]: boolean },
): IPickListGroup[] {
    return [
        {
            key: GroupIds.repositoryDefault,
            name: undefined,
            pivotId: PivotIds.branches,
        },
        viewMyBranches &&
        {
            key: GroupIds.mineBranches,
            name: Resources.VersionDropdown_MineBranches,
            pivotId: PivotIds.branches,
        },
        {
            key: GroupIds.allBranches,
            name: Resources.VersionDropdown_AllBranches,
            pivotId: PivotIds.branches,
            isLoading: loadingState[GroupIds.allBranches],
        },
        viewTagsPivot &&
        {
            key: GroupIds.allTags,
            name: undefined,
            pivotId: PivotIds.tags,
            isLoading: loadingState[GroupIds.allTags],
        },
        viewCommitsPivot &&
        {
            key: GroupIds.foundCommits,
            name: undefined,
            pivotId: PivotIds.commits,
        }
    ].filter(Boolean) as IPickListGroup[];
}

function createPivots(options: IVersionDropdownOptions): IPickListPivot[] {
    const clearAction = options && options.viewClearButton
        ? () => options.onVersionSelected && options.onVersionSelected(null)
        : undefined;

    return [
        {
            id: PivotIds.branches,
            name: Resources.VersionDropdown_PivotBranches,
            getActions: () => createActions(options.onCreateBranchClick, clearAction, !options.selectedVersionString),
            searchTextPlaceholder: Resources.VersionDropdown_BranchesSearchTextPlaceholder,
            searchNoResultsText: Resources.VersionDropdown_BranchesSearchNoResultsText,
        },
        options.viewTagsPivot &&
        {
            id: PivotIds.tags,
            name: Resources.VersionDropdown_PivotTags,
            searchTextPlaceholder: Resources.VersionDropdown_TagsSearchTextPlaceholder,
            searchNoResultsText: Resources.VersionDropdown_TagsSearchNoResultsText,
            noItemsText: Resources.VersionDropdown_TagsNoItemsText,
        },
        options.viewCommitsPivot &&
        {
            id: PivotIds.commits,
            name: Resources.VersionDropdown_PivotCommits,
            searchTextPlaceholder: Resources.VersionDropdown_CommitsSearchTextPlaceholder,
            searchNoResultsText: Resources.VersionDropdown_CommitsSearchNoResultsText,
            noItemsText: Resources.VersionDropdown_CommitsSearchMinimumCharactersError,
        },
    ].filter(Boolean) as IPickListPivot[];
}

function createActions(onCreateBranchClick?: () => void, clearAction?: () => void, clearActionDisabled?: boolean): IPickListAction[] {
    const actions: IPickListAction[] = [];

    if (onCreateBranchClick) {
        actions.push({
            name: Resources.VersionDropdown_NewBranch,
            iconProps: { iconName: "Add" },
            onClick: () => onCreateBranchClick(),
        });
    }

    if (clearAction) {
        actions.push({
            name: Resources.VersionDropdown_ClearSelection,
            iconProps: { iconName: "Cancel" },
            disabled: clearActionDisabled,
            onClick: clearAction,
        });
    }

    return actions;
}

function createRefDropdownItem(
    ref: GitRef,
    repository: GitRepository,
    currentUserId: string,
    folderFavorites: string[],
): IVersionDropdownItem {
    const friendlyName = RefUtils.getFriendlyName(ref.name);
    const isTag = RefUtils.isTag(ref.name);

    return {
        refName: ref.name,
        friendlyName,
        versionString: isTag ? RefUtils.getVersionFromTagName(friendlyName) : RefUtils.getVersionFromBranchName(friendlyName),
        kind: isTag ? "tag" : "branch",
        isMine: ref.creator && (ref.creator.id === currentUserId),
        isDefault: ref.name === repository.defaultBranch,
        isFavoritedByFolder: !isTag && belongsToFolderFavorite(ref.name, folderFavorites),
        repositoryId: repository.id,
    };
}

function belongsToFolderFavorite(refName: string, folderFavorites: string[]): boolean {
    return folderFavorites.some(folderFavorite => refName.startsWith(folderFavorite + "/"));
}

function createCommitDropdownItem(commit: GitCommitRef, repository: GitRepository): IVersionDropdownItem {
    return {
        refName: commit.commitId,
        friendlyName: RefUtils.getShortCommitId(commit.commitId) + " " + commit.comment,
        versionString: RefUtils.getVersionFromCommitHash(commit.commitId),
        kind: "commit",
        repositoryId: repository.id,
    };
}

function padEnd(text: string, character: string, length: number): string {
    if (!text || text.length >= length) {
        return text;
    } else {
        return text + Array(length - text.length + 1).join(character);
    }
}

function getItemFromVersionString(versionString: string, repository: GitRepository): IVersionDropdownItem | undefined {
    if (!versionString) {
        return;
    }

    const isTag = versionString.substring(0, 2) === "GT";
    const isCommit = versionString.substring(0, 2) === "GC";
    let friendlyName = versionString.substring(2);
    if (isCommit) {
        friendlyName = RefUtils.getShortCommitId(friendlyName);
    }

    const defaultBranch = RefUtils.getFriendlyName(repository.defaultBranch);
    return {
        refName: isTag ? RefUtils.getRefNameFromTagName(friendlyName) : RefUtils.getRefNameFromBranchName(friendlyName),
        friendlyName,
        versionString,
        kind: isCommit ? "commit" : isTag ? "tag" : "branch",
        isDefault: friendlyName === defaultBranch,
        repositoryId: repository.id,
    };
}

function getFavoriteFromArtifact(item: IVersionDropdownItem): Favorite | undefined {
    const canBeFavorited = item.kind === "branch" && !item.isDefault && !item.isMine;
    if (!canBeFavorited) {
        return undefined;
    }

    return {
        artifactId: item.refName,
        artifactName: item.friendlyName,
        artifactScope: {
            id: item.repositoryId,
            type: GitRefFavoriteArtifactConstants.artifactScopeType,
        },
        artifactType: GitRefFavoriteArtifactConstants.artifactType,
    } as Favorite;
}

function getArtifactListGroupId(item: IVersionDropdownItem): string {
    if (item.kind === "commit") {
        return GroupIds.foundCommits;
    } else if (item.kind === "tag") {
        return GroupIds.allTags;
    } else {
        return item.isDefault
            ? GroupIds.repositoryDefault
            : item.isMine || item.isFavoritedByFolder
                ? GroupIds.mineBranches
                : GroupIds.allBranches;
    }
}

function artifactComparer(item1: IVersionDropdownItem, item2: IVersionDropdownItem): number {
    return item1.friendlyName.localeCompare(item2.friendlyName, navigator.language);
}

function validateCommitsSearch(searchText: string): void {
    if (!searchText || searchText.length < minCommitCharacters) {
        throw new Error(Resources.VersionDropdown_CommitsSearchMinimumCharactersError);
    }

    if (!isValidCommitIdRegex.test(searchText)) {
        throw new Error(Resources.VersionDropdown_CommitsSearchWrongCharactersError);
    }
}

function filterCommitsInMemory(searchText: string, items: IVersionDropdownItem[]): IVersionDropdownItem[] {
    return items.filter(item => item.refName.startsWith(searchText));
}

function filterRefsInMemory(searchText: string, items: IVersionDropdownItem[]): IVersionDropdownItem[] {
    return items.filter(item => item.friendlyName.toLowerCase().indexOf(searchText.toLowerCase()) !== -1);
}

function areCommitResultsComplete(items: IVersionDropdownItem[]): boolean {
    return items.length <= maxCommitResults;
}

function areRefsResultsComplete(list: PagedList<IVersionDropdownItem>): boolean {
    return !list.continuationToken;
}

/**
 * For commits, search in the full commit ID (not only in the 8-char short one).
 * Default behavior for other kind of refs.
 */
function filterItemByText(searchText: string, item: IPickListItem, data: IVersionDropdownItem): boolean {
    const name = data.kind === "commit" ? data.refName : item.name;
    return name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
}
